import { useEffect, useCallback, useState } from "react";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import stores, { actions, useTypedSelector } from "@/stores";
import useMintHistory from "@/hooks/useMintHistory";
import bridges, { BridgeTransaction, OneClickBridge } from "@/libs/oneclick";
import useAccount from "./useAccount";

import type { EscrowDeposit } from "@/types/network";

export default function useOneClickBridge(serviceId: keyof typeof bridges) {
  const { form, changeFormField } = useVerifyClaim();
  const { address } = useAccount();

  const bridge = bridges[serviceId] as OneClickBridge;
  const ZKSyncBridge = bridges.zkSync;

  const { depositing, depositSuccess } = useTypedSelector((state) => {
    return state.bridge;
  });

  const zkSyncDeposit = useCallback(async () => {
    const depositInfo = form?.zksync as {
      chain: string;
      depositCost: number;
    };
    const paymentInfo = await ZKSyncBridge.getPaymentInfo(address as string, {
      chainId: parseInt(depositInfo.chain),
      tokenAmount: depositInfo.depositCost,
    });

    console.log("start zksync deposit", paymentInfo);
    stores.dispatch(actions.bridge.setDepositing(true));
    try {
      const transaction = await ZKSyncBridge.deposit(paymentInfo);
      stores.dispatch(actions.bridge.setDepositSuccess(true));
      return transaction;
    } finally {
      stores.dispatch(actions.bridge.setDepositing(false));
    }
  }, [address, form, serviceId]);

  const recover = useCallback(
    async (transaction: BridgeTransaction) => {
      stores.dispatch(actions.bridge.setDepositing(true));
      try {
        stores.dispatch(actions.bridge.setDepositSuccess(true));
        await bridge.oneClickBridgeConfirm(transaction);
        await bridge.waitUntilMintSuccess(transaction);
        await new Promise((resolve) => {
          setTimeout(resolve, 3000);
        });
        stores.dispatch(actions.vc.setMintSuccess(true));
        return transaction;
      } finally {
        stores.dispatch(actions.bridge.setDepositing(false));
      }
    },
    [address, form, serviceId]
  );

  const karatDeposit = useCallback(
    async (data: any) => {
      const escrowDepositInfo = form?.karat as EscrowDeposit | undefined;
      stores.dispatch(actions.bridge.setDepositing(true));

      try {
        const paymentInfo = await bridge.getPaymentInfo(address as string, {
          walletAddress: address,
          token: {
            ...escrowDepositInfo?.token,
            address: escrowDepositInfo?.token?.tokenAddress,
          },
          ...data,
        });

        console.log("start karat deposit", paymentInfo);
        stores.dispatch(actions.vc.setMintSuccess(false));
        const transaction = await bridge.deposit(paymentInfo);
        stores.dispatch(actions.bridge.setDepositSuccess(true));
        await bridge.oneClickBridgeConfirm(transaction);
        await bridge.waitUntilMintSuccess(transaction);
        await new Promise((resolve) => {
          setTimeout(resolve, 3000);
        });
        stores.dispatch(actions.vc.setMintSuccess(true));
        return transaction;
      } finally {
        stores.dispatch(actions.bridge.setDepositing(false));
      }
    },
    [address, form, serviceId]
  );

  return {
    depositing,
    depositSuccess,
    karatDeposit,
    zkSyncDeposit,
    recover,
  };
}
